/**
 * useUserProfileForm Hook
 * Reusable form hook using useReducer for User Profile forms
 */

import { useReducer, useRef, useCallback } from "react";
import {
  userProfileFormReducer,
  initialUserProfileFormState,
  USER_PROFILE_FORM_ACTION_TYPES,
} from "../utils/userProfileFormReducer";

/**
 * Custom hook for User Profile form management
 * @param {Object} initialValues - Initial form values (optional)
 * @returns {Object} Form state and handlers
 */
export const useUserProfileForm = (initialValues = {}) => {
  // Initialize state with custom values if provided
  const initialState = {
    ...initialUserProfileFormState,
    ...initialValues,
  };

  const [formState, dispatch] = useReducer(userProfileFormReducer, initialState);

  // Refs for focus management
  const passwordRef = useRef(null);
  const passwordConfirmationRef = useRef(null);

  // Personal Information Setters (Individual)
  const setFirstName = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_FIRST_NAME, payload: value });
  }, []);

  const setMiddleName = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_MIDDLE_NAME, payload: value });
  }, []);

  const setLastName = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_LAST_NAME, payload: value });
  }, []);

  // Personal Information Setters (Company)
  const setCompanyName = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_COMPANY_NAME, payload: value });
  }, []);

  const setCompanyFirstName = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_COMPANY_FIRST_NAME, payload: value });
  }, []);

  const setCompanyLastName = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_COMPANY_LAST_NAME, payload: value });
  }, []);

  const setRegistrationNumber = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_REGISTRATION_NUMBER, payload: value });
  }, []);

  const setVatId = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_VAT_ID, payload: value });
  }, []);

  const setCompanyDocument = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_COMPANY_DOCUMENT, payload: value });
  }, []);

  // Account Information Setters
  const setUserCategory = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_USER_CATEGORY, payload: value });
  }, []);

  const setUserType = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_USER_TYPE, payload: value });
  }, []);

  const setDept = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_DEPT, payload: value });
  }, []);

  const setJobTitle = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_JOB_TITLE, payload: value });
  }, []);

  const setOverheadCostType = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_OVERHEAD_COST_TYPE, payload: value });
  }, []);

  const setWxComapny = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_WX_COMPANY, payload: value });
  }, []);

  const setContract = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_CONTRACT, payload: value });
  }, []);

  const setMonthlyhours = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_MONTHLY_HOURS, payload: value });
  }, []);

  const setCurrency = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_CURRENCY, payload: value });
  }, []);

  const setCost = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_COST, payload: value });
  }, []);

  const setOverheadCost = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_OVERHEAD_COST, payload: value });
  }, []);

  const setVat = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_VAT, payload: value });
  }, []);

  // Location Information Setters
  const setCountry = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_COUNTRY, payload: value });
  }, []);

  const setState = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_STATE, payload: value });
  }, []);

  const setCity = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_CITY, payload: value });
  }, []);

  const setAddress = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_ADDRESS, payload: value });
  }, []);

  const setPostCode = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_POST_CODE, payload: value });
  }, []);

  const setMobileNo = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_MOBILE_NO, payload: value });
  }, []);

  // Password Setters
  const setPassword = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_PASSWORD, payload: value });
  }, []);

  const setPasswordConfirmation = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_PASSWORD_CONFIRMATION, payload: value });
  }, []);

  const setShowPassword = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_SHOW_PASSWORD, payload: value });
  }, []);

  // Date Setters
  const setSelectedDate = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_SELECTED_DATE, payload: value });
  }, []);

  const setJoiningDate = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_JOINING_DATE, payload: value });
  }, []);

  const setPassport = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_PASSPORT, payload: value });
  }, []);

  // Validation Setters
  const setPasswordError = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_PASSWORD_ERROR, payload: value });
  }, []);

  const setPasswordMatchError = useCallback((value) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_PASSWORD_MATCH_ERROR, payload: value });
  }, []);

  // Reset functions
  const resetForm = useCallback(() => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.RESET_FORM });
  }, []);

  const resetValidation = useCallback(() => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.RESET_VALIDATION });
  }, []);

  // Bulk update
  const setFormData = useCallback((data) => {
    dispatch({ type: USER_PROFILE_FORM_ACTION_TYPES.SET_FORM_DATA, payload: data });
  }, []);

  return {
    // Form state
    formState,
    
    // Individual field values (for backward compatibility)
    firstName: formState.firstName,
    middleName: formState.middleName,
    lastName: formState.lastName,
    companyName: formState.companyName,
    companyFirstName: formState.companyFirstName,
    companyLastName: formState.companyLastName,
    registrationNumber: formState.registrationNumber,
    vatId: formState.vatId,
    companyDocument: formState.companyDocument,
    userCategory: formState.userCategory,
    userType: formState.userType,
    dept: formState.dept,
    jobTitle: formState.jobTitle,
    overheadCostType: formState.overheadCostType,
    wxComapny: formState.wxComapny,
    contract: formState.contract,
    monthlyhours: formState.monthlyhours,
    currency: formState.currency,
    cost: formState.cost,
    overheadCost: formState.overheadCost,
    vat: formState.vat,
    country: formState.country,
    state: formState.state,
    city: formState.city,
    address: formState.address,
    postCode: formState.postCode,
    mobileNo: formState.mobileNo,
    password: formState.password,
    passwordConfirmation: formState.passwordConfirmation,
    showPassword: formState.showPassword,
    selectedDate: formState.selectedDate,
    joiningDate: formState.joiningDate,
    passport: formState.passport,
    passwordError: formState.passwordError,
    passwordMatchError: formState.passwordMatchError,
    
    // Refs
    passwordRef,
    passwordConfirmationRef,
    
    // Setters
    setFirstName,
    setMiddleName,
    setLastName,
    setCompanyName,
    setCompanyFirstName,
    setCompanyLastName,
    setRegistrationNumber,
    setVatId,
    setCompanyDocument,
    setUserCategory,
    setUserType,
    setDept,
    setJobTitle,
    setOverheadCostType,
    setWxComapny,
    setContract,
    setMonthlyhours,
    setCurrency,
    setCost,
    setOverheadCost,
    setVat,
    setCountry,
    setState,
    setCity,
    setAddress,
    setPostCode,
    setMobileNo,
    setPassword,
    setPasswordConfirmation,
    setShowPassword,
    setSelectedDate,
    setJoiningDate,
    setPassport,
    setPasswordError,
    setPasswordMatchError,
    
    // Reset functions
    resetForm,
    resetValidation,
    setFormData,
  };
};
